import { z } from 'zod';
import type { ScraperType } from './quote-calculation-engine';

/**
 * PROBIG-Offer-Checkliste (ausfüllbares PDF, Version 2.0): die Formularfelder,
 * wie der Kunde sie ausfüllt, als Grundlage für eine neue Kalkulation (ADR 0112).
 * Gelesen wird auf dem Server (pdf-lib, AcroForm); hier steht nur die Abbildung
 * der Feldnamen — rein und ohne PDF-Bibliothek testbar.
 */

export type OfferChecklistFieldValue = string | boolean;

/** Optionen der Checkliste: Feldname (ohne `opt_`) → Bezeichnung und Suchbegriffe für den Artikelstamm. */
export const OFFER_CHECKLIST_OPTIONS = {
  flightalignmentmonitoringsyste: { de: 'Gleichlaufüberwachung', en: 'Flight alignment monitoring system', terms: ['Gleichlaufüberwachung Standard', 'Gleichlaufüberwachung', 'Flight alignment'] },
  nonmetallicscrewconveyor: { de: 'Kunststoffschnecke', en: 'Non-metallic screw conveyor', terms: ['KSSR', 'Kunststoffschnecke', 'screw'] },
  certificateatex: { de: 'Zertifikat ATEX', en: 'Certificate ATEX', terms: ['ATEX'] },
  mechanicchaintensioningdevice: { de: 'Mechanische Spannvorrichtung', en: 'Mechanic chain tensioning device', terms: ['Spannvorrichtung mit Feder', 'Mechanische Spannvorrichtung'] },
  gasproofmotorconsole: { de: 'Gasdichte Motorkonsole', en: 'Gas proof motor / console', terms: ['gasdicht', 'gas proof'] },
  materialcertificate21: { de: 'Materialzeugnis 2.1', en: 'Material certificate 2.1', terms: ['Materialzeugnis 2.1', 'certificate 2.1'] },
  semiautomaticchaintensioningde: { de: 'Halbautomatische Spannvorrichtung', en: 'Semi-automatic chain tensioning device', terms: ['halbautomatisch', 'semi-automatic'] },
  toothprotectionedgedsprockets: { de: 'Zahnschutz Kettenräder', en: 'Tooth protection edged sprockets', terms: ['Zahnschutz', 'tooth protection'] },
  materialcertificate31: { de: 'Materialzeugnis 3.1', en: 'Material certificate 3.1', terms: ['Materialzeugnis 3.1', 'certificate 3.1'] },
  automaticchaintensiondevice: { de: 'Automatische Spannvorrichtung', en: 'Automatic chain tension device', terms: ['automatische Spannvorrichtung', 'automatic chain tension'] },
  itp: { de: 'Prüfplan (ITP)', en: 'ITP', terms: ['ITP'] },
  sparepartpackage: { de: 'Ersatzteilpaket', en: 'Spare part package', terms: ['Ersatzteilpaket', 'Sparepart'] },
  activepowermeasurement: { de: 'Wirkleistungsmessung', en: 'Active power measurement', terms: ['Wirkleistungsmesser', 'Power mesurement', 'power measurement'] },
  qcp: { de: 'Qualitätsplan (QCP)', en: 'QCP', terms: ['QCP'] },
  seafreightpackaging: { de: 'Seefrachtverpackung', en: 'Sea freight packaging', terms: ['Holzkisten Seefracht', 'Seefracht'] },
  scumpipe: { de: 'Skimrinne', en: 'Scum-pipe', terms: ['Skimrinne', 'Scum-pipe'] },
  fat: { de: 'Werksabnahme (FAT)', en: 'FAT', terms: ['FAT'] },
  extractionbox: { de: 'Abzugskasten', en: 'Extraction box', terms: ['Abzugskasten', 'Extraction box'] },
} as const;
export type OfferChecklistOptionKey = keyof typeof OFFER_CHECKLIST_OPTIONS;

/** Lieferumfang der Checkliste (`scp_…`). */
export const OFFER_CHECKLIST_SCOPE = {
  nonmetalliccomponents: { de: 'Kunststoffkomponenten', en: 'Non-metallic components' },
  metalliccomponents: { de: 'Stahlkomponenten', en: 'Metallic components' },
  driveunit: { de: 'Antriebseinheit', en: 'Drive unit' },
  floatingsludgeremoval: { de: 'Schwimmschlammabzug', en: 'Floating sludge removal' },
  installation: { de: 'Montage', en: 'Installation' },
  supervisorforinstallation: { de: 'Supervisor für die Montage', en: 'Supervisor for installation' },
} as const;
export type OfferChecklistScopeKey = keyof typeof OFFER_CHECKLIST_SCOPE;

const SCRAPER_FIELDS: Record<string, { type: ScraperType; label: string }> = {
  sc_4shaftscraper: { type: 'FOUR_SHAFT', label: '4-shaft scraper' },
  sc_3shaftscraper: { type: 'THREE_SHAFT', label: '3-shaft scraper' },
  sc_2shaftbottomscraper: { type: 'TWO_SHAFT_BOTTOM', label: '2-shaft bottom scraper' },
  sc_2shaftsurfacescraper: { type: 'TWO_SHAFT_SURFACE', label: '2-shaft surface scraper' },
  sc_gritchamberscraper: { type: 'OTHER', label: 'Grit chamber scraper' },
  sc_greasescraper: { type: 'OTHER', label: 'Grease scraper' },
  sc_floatingsludgeextraction: { type: 'OTHER', label: 'Floating sludge extraction' },
  scSpecial: { type: 'OTHER', label: 'Special design' },
};
const MATERIALS: Record<string, string> = {
  mat_1430114307: '1.4301 / 1.4307',
  mat_14404: '1.4404',
  mat_14571: '1.4571',
  mat_duplex: 'Duplex',
  mat_superduplex: 'Superduplex',
  mat_grp: 'GRP',
};
const PHASES: Record<string, string> = { phase_tender: 'Tender', phase_budgetphase: 'Budget Phase', phase_design: 'Design' };
const CUSTOMER_TYPES: Record<string, string> = { cust_endcustomer: 'End Customer', cust_consultant: 'Consultant', cust_contractor: 'Contractor' };

export const offerChecklistSchema = z.object({
  projectName: z.string(),
  application: z.string(),
  phases: z.array(z.string()),
  customerTypes: z.array(z.string()),
  country: z.string(),
  incoterm: z.string(),
  offerDeadline: z.string(),
  /** Becken mit derselben Räumeranlage. */
  basinCount: z.number().int().nullable(),
  lengthM: z.number().nullable(),
  widthM: z.number().nullable(),
  heightM: z.number().nullable(),
  waterLevelM: z.number().nullable(),
  plantTypes: z.array(z.string()),
  scraperType: z.enum(['FOUR_SHAFT', 'THREE_SHAFT', 'TWO_SHAFT_BOTTOM', 'TWO_SHAFT_SURFACE', 'OTHER']).nullable(),
  /** Angekreuzte Bauformen im Klartext (auch mehrere, z. B. Sonderbau mit Beschreibung). */
  scraperDesigns: z.array(z.string()),
  materials: z.array(z.string()),
  scope: z.array(z.enum(Object.keys(OFFER_CHECKLIST_SCOPE) as [OfferChecklistScopeKey, ...OfferChecklistScopeKey[]])),
  options: z.array(z.enum(Object.keys(OFFER_CHECKLIST_OPTIONS) as [OfferChecklistOptionKey, ...OfferChecklistOptionKey[]])),
  contactEmail: z.string(),
  contactPhone: z.string(),
  notes: z.string(),
});
export type OfferChecklist = z.infer<typeof offerChecklistSchema>;

/** „6.75", „6,75", „6,75 m" → 6.75; Unlesbares → null. */
function number(value: OfferChecklistFieldValue | undefined): number | null {
  if (typeof value !== 'string') return null;
  const match = /-?\d+(?:[.,]\d+)?/.exec(value.replace(/\s/g, ''));
  return match ? Number(match[0].replace(',', '.')) : null;
}
const text = (value: OfferChecklistFieldValue | undefined) => (typeof value === 'string' ? value.replace(/\r\n?/g, '\n').trim() : '');
const checked = (fields: Record<string, OfferChecklistFieldValue>, map: Record<string, string>) => Object.entries(map).filter(([key]) => fields[key] === true).map(([, label]) => label);

/** Ist das Formular eine PROBIG-Offer-Checkliste? (Mindestens Projektname und Maßfelder.) */
export function isOfferChecklist(fields: Record<string, OfferChecklistFieldValue>): boolean {
  return ['projectName', 'length', 'width', 'noOfTanks'].every((key) => key in fields);
}

/** Formularfelder → Daten der Checkliste. */
export function parseOfferChecklist(fields: Record<string, OfferChecklistFieldValue>): OfferChecklist {
  const designs = Object.entries(SCRAPER_FIELDS).filter(([key]) => fields[key] === true);
  const special = text(fields.scSpecialDesc);
  // Eine Standardbauform gewinnt; nur Sonderbauformen → Sonderanlage.
  const standard = designs.find(([, entry]) => entry.type !== 'OTHER');
  return {
    projectName: text(fields.projectName),
    application: text(fields.application),
    phases: checked(fields, PHASES),
    customerTypes: checked(fields, CUSTOMER_TYPES),
    country: text(fields.country),
    incoterm: text(fields.incoterm),
    offerDeadline: text(fields.offerDeadline),
    basinCount: (() => {
      const value = number(fields.noOfTanks);
      return value === null ? null : Math.max(1, Math.round(value));
    })(),
    lengthM: number(fields.length),
    widthM: number(fields.width),
    heightM: number(fields.height),
    waterLevelM: number(fields.waterLevel),
    plantTypes: checked(fields, { typeIndustrial: 'Industrial', typeMunicipal: 'Municipal' }),
    scraperType: standard ? standard[1].type : designs.length ? 'OTHER' : null,
    scraperDesigns: designs.map(([key, entry]) => (key === 'scSpecial' && special ? `${entry.label}: ${special}` : entry.label)),
    materials: checked(fields, MATERIALS),
    scope: (Object.keys(OFFER_CHECKLIST_SCOPE) as OfferChecklistScopeKey[]).filter((key) => fields[`scp_${key}`] === true),
    options: (Object.keys(OFFER_CHECKLIST_OPTIONS) as OfferChecklistOptionKey[]).filter((key) => fields[`opt_${key}`] === true),
    contactEmail: text(fields.contactEmail),
    contactPhone: text(fields.contactPhone),
    notes: text(fields.notes),
  };
}

/** Notiz der Kalkulation: alles aus der Checkliste, was keine eigene Spalte hat. */
export function offerChecklistNotes(checklist: OfferChecklist, fileName: string): string {
  const line = (label: string, value: string | string[]) => {
    const joined = Array.isArray(value) ? value.join(', ') : value;
    return joined ? `${label}: ${joined}` : null;
  };
  const scope = checklist.scope.map((key) => OFFER_CHECKLIST_SCOPE[key].de);
  const options = checklist.options.map((key) => OFFER_CHECKLIST_OPTIONS[key].de);
  return [
    `Aus der Offer-Checkliste „${fileName}":`,
    line('Anwendung', checklist.application),
    line('Projektphase', checklist.phases),
    line('Kunde ist', checklist.customerTypes),
    line('Land', checklist.country),
    line('Incoterm', checklist.incoterm),
    line('Angebotsfrist', checklist.offerDeadline),
    line('Anlagentyp', checklist.plantTypes),
    line('Bauform', checklist.scraperDesigns),
    line('Material Stahlteile', checklist.materials),
    line('Lieferumfang', scope),
    line('Optionen', options),
    line('Kontakt', [checklist.contactEmail, checklist.contactPhone].filter(Boolean)),
    checklist.notes ? `\nAnmerkungen des Kunden:\n${checklist.notes}` : null,
  ]
    .filter((entry): entry is string => entry !== null)
    .join('\n');
}

/** Firmenname zum Vorschlagen aus der Kontakt-Adresse („omar@sulzer.com" → „sulzer"). */
export function offerChecklistCustomerHint(checklist: OfferChecklist): string {
  const domain = checklist.contactEmail.split('@')[1] ?? '';
  const parts = domain.toLowerCase().split('.').filter(Boolean);
  return parts.length >= 2 ? parts[parts.length - 2]! : '';
}

/**
 * Artikel für eine Option der Checkliste: der meistverwendete, dessen Name oder
 * Angebotstext einen Suchbegriff enthält (ohne Groß-/Kleinschreibung); sonst null.
 */
export function matchOfferChecklistOption<T extends { name: string; offerText: string; uses: number }>(key: OfferChecklistOptionKey, candidates: readonly T[]): T | null {
  for (const term of OFFER_CHECKLIST_OPTIONS[key].terms) {
    // Ganze Wörter: „ITP" oder „FAT" sollen nicht in anderen Namen stecken („Splitplate").
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`(^|[^\\p{L}\\p{N}])${escaped}($|[^\\p{L}\\p{N}])`, 'iu');
    const hits = candidates.filter((candidate) => pattern.test(`${candidate.name} ${candidate.offerText}`));
    if (hits.length) return [...hits].sort((a, b) => b.uses - a.uses)[0]!;
  }
  return null;
}

/** Antwort beim Lesen einer Checkliste: Daten, Kunden-Hinweis und je Option der vorgeschlagene Artikel. */
export const offerChecklistParseResponseSchema = z.object({
  checklist: offerChecklistSchema,
  customerHint: z.string(),
  options: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      articleId: z.string().uuid().nullable(),
      articleNumber: z.string().nullable(),
      articleName: z.string().nullable(),
    }),
  ),
});
export type OfferChecklistParseResponse = z.infer<typeof offerChecklistParseResponseSchema>;

/** Beim Anlegen mitgegeben: Positionen und Notiz kommen dann aus der Checkliste. */
export const createFromOfferChecklistSchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  checklist: offerChecklistSchema,
});
